import React, { useState, useMemo, useCallback } from 'react';
import { Trash2 } from 'lucide-react';

/**
 * Shared helpers for comptabilite ERP list pages (status badges + row actions).
 */

/** Create/edit modal field grid — 3 columns on md+ */
export const COMPTABILITE_FORM_GRID = 'grid grid-cols-1 md:grid-cols-3 gap-4';

export { useComptabiliteFiscalYearOptions } from './useComptabiliteFiscalYearOptions';
export { fiscalYearSelectOptionsWithFallback, dispatchFiscalYearRefresh } from './fiscalYearUtils';
export { buildChartAccountOptions, dispatchChartAccountsRefresh } from './chartAccountOptions';
export { loadAnalyticalSelectOptions, dispatchAnalyticalCodesRefresh } from './comptabiliteAnalyticalOptions';

/** Row selection state aligned with Products.js list-table UX. */
export function useComptabiliteListRowSelection(filteredItems, getId = (row) => row.id) {
  const [selectedRecords, setSelectedRecords] = useState([]);

  const filteredIds = useMemo(
    () => filteredItems.map(getId),
    [filteredItems, getId],
  );

  const allRowsSelected = filteredIds.length > 0
    && filteredIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = useCallback(() => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredIds);
    }
  }, [allRowsSelected, someRowsSelected, filteredIds]);

  const handleSelectRecord = useCallback((id) => {
    setSelectedRecords((prev) => (
      prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]
    ));
  }, []);

  const clearSelection = useCallback(() => setSelectedRecords([]), []);

  return {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  };
}

/** Bulk-delete chip for QawaemPageTitleWithFilters / QawaemErpListPageShell filterActions. */
export function ComptabiliteBulkDeleteFilterAction({
  count,
  onClick,
  label,
  title,
}) {
  if (!count) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
      title={title}
    >
      <Trash2 size={16} />
      <span>{label}</span>
    </button>
  );
}

export const COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS = 'px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle';

/** Map { value, labelKey?, label?, icon, style } defs to QawaemClickableStatusBadge options. */
export function mapComptabiliteStatusOptions(definitions = [], p) {
  return definitions.map((opt) => ({
    value: opt.value,
    label: opt.label ?? (opt.labelKey ? p(opt.labelKey) : opt.value),
    icon: opt.icon,
    style: opt.style,
  }));
}

/**
 * Qawaem column-picker state for comptabilite list tables.
 * @param {Array<{ key: string, labelKey: string, required?: boolean }>} columnDefs
 * @param {(key: string, opts?: object) => string} p
 */
export function useComptabiliteTableColumnVisibility(columnDefs, p) {
  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(columnDefs.map((c) => [c.key, true])),
  );

  const qawaemColumnDefs = useMemo(
    () => columnDefs.map((col) => ({
      key: col.key,
      label: p(col.labelKey),
      checked: columnVisibility[col.key] !== false,
      required: col.required,
    })),
    [columnDefs, columnVisibility, p],
  );

  const handleToggleColumn = useCallback((key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;

  const isColVisible = useCallback(
    (key) => columnVisibility[key] !== false,
    [columnVisibility],
  );

  const visibleDataColumnCount = useMemo(
    () => columnDefs.filter((c) => columnVisibility[c.key] !== false).length,
    [columnDefs, columnVisibility],
  );

  return {
    qawaemColumnDefs,
    handleToggleColumn,
    qawaemVisibleColumnCount,
    isColVisible,
    visibleDataColumnCount,
  };
}
